import { makeAutoObservable, when } from 'mobx'
import { type IDropdownItem, type ITooltipProps } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { AddPaymentMethodStore } from 'entities/Billing/ui/AddPaymentMethodModal'
import { type Source } from 'entities/Billing/model/Source'
import type { BillingStore } from 'entities/Billing'
import type { SubscriptionStore } from 'entities/Subscription'

type IDropdownCardsStoreProps = {
  activeId?: string
  hideId?: string
  labelText?: string
  labelTooltip?: ITooltipProps
  withoutDefaultPresetCard?: boolean
  enableAch?: boolean
  isFranchise?: boolean
}

export class DropdownCardsStore {
  private _activeId: string | null = null
  private _hideId: string | null = null
  private _withoutDefaultPresetCard = false
  private _labelText = 'Payment method'
  private _labelTooltip: ITooltipProps | null = null
  private _enableAch = false
  private _paymentError = ''
  private _isFranchise = false

  constructor(
    private _billingStore: BillingStore,
    private _subscriptionStore: SubscriptionStore,
    props?: IDropdownCardsStoreProps
  ) {
    makeAutoObservable(this)

    if (props?.activeId) {
      this._activeId = props.activeId
    }
    if (props?.hideId) {
      this._hideId = props.hideId
    }
    if (props?.labelText) {
      this._labelText = props.labelText
    }
    if (props?.labelTooltip) {
      this._labelTooltip = props.labelTooltip
    }
    if (props?.withoutDefaultPresetCard) {
      this._withoutDefaultPresetCard = props.withoutDefaultPresetCard
    }
    if (props?.enableAch) {
      this._enableAch = props.enableAch
    }
    if (props?.isFranchise) {
      this._isFranchise = props.isFranchise
    }
  }

  setFirstActive = () => {
    if (!this.sources.length) return
    let franchisePrimarySource = null

    if (this._isFranchise) {
      franchisePrimarySource = this.sources.find((source) => source.franchise_primary)
    }

    const primarySource = this.sources.find((source) => source.primary)

    const targetSource =
      this._isFranchise && franchisePrimarySource
        ? franchisePrimarySource
        : primarySource || this.defaultPaymentMethod || this.sources[0]

    if (targetSource) {
      this.setActiveId(targetSource.id)
    }
  }

  loadSources = async () => {
    try {
      if (this._billingStore.sourcesLoading) {
        await Promise.all([
          when(() => !this._billingStore.sourcesLoading),
          this._subscriptionStore.initPromise,
        ])
      } else {
        await Promise.all([
          this._billingStore.fetchBillingSources(),
          this._subscriptionStore.initPromise,
        ])
      }

      if (this._withoutDefaultPresetCard) return
      if (this.sources.length) {
        this.setFirstActive()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  onChange = (item: IDropdownItem) => {
    this.setActiveId(item.id as string)
    this.clearPaymentError()
  }

  setActiveId = (value: typeof this._activeId) => {
    this._activeId = value
  }

  onAddPaymentMethod = () => {
    new AddPaymentMethodStore(
      (source) => {
        this._activeId = source.id
        this.clearPaymentError()
      },
      this._billingStore,
      ZIndex.OVERLAY_TOP_120
    )
  }

  setHideId = (value: typeof this._hideId) => {
    if (this._activeId === value) {
      this._activeId = null
    }
    this._hideId = value
  }

  setPaymentError = (value: string) => {
    this._paymentError = value
  }

  clearPaymentError = () => {
    this._paymentError = ''
  }

  get defaultPaymentMethodId() {
    return this._subscriptionStore?.defaultPaymentMethodId
  }

  get activeItem(): Source | null {
    if (this.isFixedACHPayment) {
      return this.defaultPaymentMethod
    }

    return (this._activeId && this._billingStore.getSources(this._activeId)) || null
  }

  get sources() {
    if (this._enableAch) {
      const allSources = this._billingStore.sources.filter((source) => source.id !== this._hideId)

      if (this._isFranchise && !this.isFixedACHPayment) {
        return allSources.filter((source) => source.object !== 'payment_method')
      }

      return allSources
    }

    return this._billingStore.sourcesSimple.filter((source) => source.id !== this._hideId)
  }

  get activeId() {
    if (this.isFixedACHPayment) {
      return this.defaultPaymentMethod?.id ?? null
    }
    return this._activeId
  }

  get labelText() {
    return this._labelText
  }

  get labelTooltip() {
    return this._labelTooltip
  }

  get paymentError() {
    return this._paymentError
  }

  get defaultPaymentMethod() {
    if (this._isFranchise) {
      const defaultACHSource = this._billingStore.sources.find((source) => source.franchise_primary)

      if (defaultACHSource?.object === 'payment_method') {
        return defaultACHSource
      }

      const defaultCardSource = this._billingStore.sources.find((source) => source.primary)

      if (defaultCardSource?.object === 'card') {
        return defaultCardSource
      }
    }

    return this.defaultPaymentMethodId
      ? this._billingStore.getSources(this.defaultPaymentMethodId)
      : null
  }

  get isFixedACHPayment() {
    if (!this._enableAch) return false

    return !!(this.defaultPaymentMethod?.object === 'payment_method')
  }
}
